import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import { products, aiSystems } from '@/data/products';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function AiSystemCard({ system, delay = 0 }) {
  const flagship = system.slug === 'scheduling-agent';
  return (
    <RevealSection delay={delay}>
      <div
        data-testid={`product-card-${system.slug}`}
        className={`h-full flex flex-col p-[30px] md:p-[50px] rounded-[10px] bg-km-card-gradient ${
          flagship
            ? 'border-2 border-km-gold/30 shadow-km-gold-soft'
            : 'border-2 border-km-cyan-2/30'
        }`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full border font-inter text-[11px] font-semibold tracking-[0.15em] uppercase ${
              flagship
                ? 'border-km-gold/30 bg-km-gold/10 text-km-gold'
                : 'border-km-cyan-2/30 bg-km-cyan-2/10 text-km-cyan-1'
            }`}
          >
            {system.status}
          </span>
          <span className="font-inter text-[11px] font-semibold tracking-[0.15em] uppercase text-km-gray-5">
            {system.category}
          </span>
        </div>
        <h2 className="mt-6 font-inter font-bold text-[26px] md:text-[38px] leading-[1.1] tracking-tight text-km-white">
          {system.name}
        </h2>
        <p className="mt-4 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3]">
          {system.tagline}
        </p>
        <p className="mt-4 font-inter font-medium text-sm text-km-gray-5 leading-[1.3]">
          {system.description}
        </p>
        <ul className="mt-8 space-y-3 flex-1">
          {system.features.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <Check className="w-4 h-4 text-km-gold mt-0.5 flex-shrink-0" />
              <span className="font-inter font-medium text-sm text-km-white/80">{item}</span>
            </li>
          ))}
        </ul>
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <a
            href={system.primaryCta.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${system.slug}-primary-cta`}
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer"
          >
            {system.primaryCta.label}
          </a>
          {system.secondaryCta && (
            <a
              href={system.secondaryCta.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${system.slug}-secondary-cta`}
              className="inline-flex items-center gap-1.5 font-inter font-medium text-sm text-km-gray-5 hover:text-km-gold transition-colors cursor-pointer"
            >
              {system.secondaryCta.label} <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </RevealSection>
  );
}

function ToolkitLead() {
  const product = products.find((p) => p.slug === 'high-stakes-narrative-pack');

  return (
    <RevealSection>
      <Link
        to={`/products/${product.slug}`}
        data-testid={`product-card-${product.slug}`}
        className="group block p-[30px] md:p-[50px] rounded-[10px] bg-km-card-gradient border-2 border-km-gold/30 hover:border-km-gold/50 transition-colors duration-300 cursor-pointer"
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-10">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-km-gold/30 bg-km-gold/10 font-inter text-[11px] font-semibold tracking-[0.15em] uppercase text-km-gold">
                Available Now
              </span>
              <span className="font-inter text-[11px] font-semibold tracking-[0.15em] uppercase text-km-gray-5">
                Notion Toolkit
              </span>
            </div>
            <h2 className="mt-6 font-inter font-bold text-[26px] md:text-[38px] leading-[1.1] tracking-tight text-km-white group-hover:text-km-gold transition-colors duration-300">
              {product.name}
            </h2>
            <p className="mt-4 font-inter font-medium text-sm md:text-base text-km-gray-5 leading-[1.3] max-w-2xl">
              {product.description}
            </p>
          </div>
          <div className="flex flex-row lg:flex-col items-center lg:items-end gap-4 lg:text-right">
            <p className="font-inter font-bold text-[28px] md:text-[38px] text-km-white">
              ${product.price} <span className="text-base font-medium text-km-gray-5">CAD</span>
            </p>
            <span className="inline-flex items-center gap-2 font-inter font-medium text-sm text-km-gray-5 group-hover:text-km-gold transition-colors duration-300">
              View details <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            </span>
          </div>
        </div>
      </Link>
    </RevealSection>
  );
}

function ComingSoonStrip() {
  const comingSoon = products.filter((p) => p.comingSoon);

  return (
    <div>
      <RevealSection>
        <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">
          More toolkits coming
        </h2>
      </RevealSection>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[20px]">
        {comingSoon.map((product, i) => (
          <RevealSection key={product.id} delay={i * 80}>
            <Link
              to={`/products/${product.slug}`}
              data-testid={`product-card-${product.slug}`}
              className="block h-full p-[16px] md:p-[30px] rounded-[10px] border border-km-gray-3 bg-km-gray-2 hover:border-km-gray-4 transition-colors duration-300 cursor-pointer"
            >
              <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-km-gray-4/50 bg-km-gray-4/20 font-inter text-[10px] font-semibold tracking-[0.15em] uppercase text-km-gray-5">
                Coming Soon
              </span>
              <h3 className="mt-4 font-inter font-medium text-base text-km-white">{product.name}</h3>
              <p className="mt-2 font-inter font-medium text-sm text-km-gray-5 leading-[1.3]">{product.tagline}</p>
            </Link>
          </RevealSection>
        ))}
      </div>
    </div>
  );
}

export default function Products() {
  return (
    <div data-testid="products-page" className="px-6 lg:px-8 pt-[80px] pb-[100px] md:pt-[120px] md:pb-[120px]">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">Products</p>
          <h1 className="font-inter font-bold text-[38px] md:text-[54px] lg:text-[68px] leading-[1.1] tracking-tight text-km-white">
            AI systems and toolkits.
          </h1>
          <p className="mt-6 font-inter font-medium text-lg text-km-gray-5 leading-[1.3] max-w-2xl">
            The agent systems we build and deploy, and the structured toolkits we ship. The High-Stakes Narrative Pack is available now.
          </p>
        </RevealSection>

        <section className="mt-[60px] md:mt-[80px]">
          <RevealSection>
            <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-8">AI Agents</p>
          </RevealSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[30px]">
            {aiSystems.map((system, i) => (
              <AiSystemCard key={system.slug} system={system} delay={i * 120} />
            ))}
          </div>
        </section>

        <section className="mt-[60px] md:mt-[80px]">
          <RevealSection>
            <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-8">Toolkits</p>
          </RevealSection>
          <ToolkitLead />
        </section>

        <section className="mt-[60px] md:mt-[80px]">
          <ComingSoonStrip />
        </section>
      </div>
    </div>
  );
}
